import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { MaterialRow, ReturnCreateRequest, ReturnReasonType, ReturnType, SupplierRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Card, Form, Input, InputNumber, Select, Space, Table, Typography } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hq } from '../api/client';
import { useAuthStore } from '../stores/auth';

interface ItemValues {
  materialId: string;
  materialName?: string;
  unitId: string;
  unitName?: string;
  batchNo?: string;
  buyQty: number;
  returnQty: number;
  price: number;
  qualityDesc?: string;
}

interface FormValues {
  returnType: ReturnType;
  reasonType: ReturnReasonType;
  sourceBizType?: string;
  sourceBizNo?: string;
  supplierId?: string;
  supplierName?: string;
  applyDeptId: string;
  returnWarehouseId: string;
  remark?: string;
  items: ItemValues[];
}

/**
 * 物资快搜。手填 material_id 写错不会报错，但退错货、归错码都要到结算阶段才发现，
 * 所以改成按名称/编码搜索，选中后把品名与基本单位一并回填（单位不让人工拍）。
 */
function MaterialSelect({
  value,
  onChange,
  onPick
}: {
  value?: string;
  onChange?: (value?: string) => void;
  onPick?: (material?: MaterialRow) => void;
}) {
  const [keyword, setKeyword] = useState('');
  const options = useQuery({
    queryKey: ['return', 'material', keyword],
    queryFn: () => hq.material.list({ keyword: keyword || undefined, size: 20 })
  });
  const rows = options.data?.items ?? [];
  return (
    <Select
      showSearch
      value={value}
      placeholder="搜索名称 / 编码"
      filterOption={false}
      onSearch={setKeyword}
      onChange={(next) => {
        onChange?.(next);
        onPick?.(rows.find((row) => row.id === next));
      }}
      loading={options.isFetching}
      options={rows.map((row) => ({ value: row.id, label: `${row.materialName ?? ''}（${row.materialCode ?? ''}）` }))}
    />
  );
}

/** 供应商快搜（同上，只用于“责任供应商”）。 */
function SupplierSelect({
  value,
  onChange,
  onPick
}: {
  value?: string;
  onChange?: (value?: string) => void;
  onPick?: (supplier?: SupplierRow) => void;
}) {
  const [keyword, setKeyword] = useState('');
  const options = useQuery({
    queryKey: ['return', 'supplier', keyword],
    queryFn: () => hq.catalog.suppliers({ keyword: keyword || undefined, size: 20 })
  });
  const rows = options.data?.items ?? [];
  return (
    <Select
      allowClear
      showSearch
      value={value}
      placeholder="搜索供应商名称 / 编码"
      filterOption={false}
      onSearch={setKeyword}
      onChange={(next) => {
        onChange?.(next);
        onPick?.(rows.find((row) => row.id === next));
      }}
      loading={options.isFetching}
      options={rows.map((row) => ({ value: row.id, label: `${row.supplierName}（${row.supplierCode}）` }))}
    />
  );
}

/**
 * 新建退换货：保存即为 NEW，后续在详情页依次做取件 / 确认回仓 / 责任判定。
 */
export default function ReturnCreate() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const profile = useAuthStore((state) => state.profile);
  const [form] = Form.useForm<FormValues>();

  const depts = useQuery({ queryKey: ['return', 'dept-options'], queryFn: () => hq.orgStructure.dept.list({}) });
  const warehouses = useQuery({ queryKey: ['return', 'warehouse-options'], queryFn: () => hq.orgStructure.warehouse.list({}) });

  const createMutation = useMutation({
    mutationFn: (payload: ReturnCreateRequest) => hq.returns.create(payload),
    onSuccess: (detail) => {
      message.success(`退货单已保存：${detail.returnOrder.bizNo}`);
      navigate(`/return/${detail.returnOrder.id}`, { replace: true });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '提交失败，请稍后重试');
    }
  });

  const onFinish = (values: FormValues) => {
    const payload: ReturnCreateRequest = {
      returnType: values.returnType,
      reasonType: values.reasonType,
      sourceBizType: values.sourceBizType || undefined,
      sourceBizNo: values.sourceBizNo || undefined,
      supplierId: values.supplierId || undefined,
      supplierName: values.supplierName || undefined,
      applyDeptId: values.applyDeptId || undefined,
      returnWarehouseId: values.returnWarehouseId || undefined,
      remark: values.remark || undefined,
      items: values.items.map((item) => ({
        materialId: item.materialId,
        materialName: item.materialName || undefined,
        unitId: item.unitId || undefined,
        unitName: item.unitName || undefined,
        batchNo: item.batchNo || undefined,
        buyQty: item.buyQty ?? 0,
        returnQty: item.returnQty,
        price: item.price ?? 0,
        qualityDesc: item.qualityDesc || undefined
      }))
    };
    createMutation.mutate(payload);
  };

  return (
    <Card className="page-card" title="新建退换货">
      <Form<FormValues>
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          returnType: 'RETURN_REFUND',
          reasonType: 'QUALITY',
          applyDeptId: profile?.deptId ?? '',
          items: [{ materialId: '', unitId: '', buyQty: 0, returnQty: 1, price: 0 }]
        }}
      >
        <Space size={16} wrap>
          <Form.Item name="returnType" label="退货类型" rules={[{ required: true, message: '必填' }]}>
            <Select
              style={{ width: 160 }}
              options={[
                { value: 'RETURN_REFUND', label: '退货退款' },
                { value: 'EXCHANGE', label: '换货' },
                { value: 'DISCOUNT', label: '折让' }
              ]}
            />
          </Form.Item>
          <Form.Item name="reasonType" label="退货原因" rules={[{ required: true, message: '必填' }]}>
            <Select
              style={{ width: 160 }}
              options={[
                { value: 'QUALITY', label: '质量问题' },
                { value: 'WRONG_ITEM', label: '错发/漏发' },
                { value: 'NEAR_EXPIRY', label: '近效期' },
                { value: 'DAMAGE', label: '破损' },
                { value: 'OVER_ORDER', label: '超量配送' },
                { value: 'POLICY', label: '政策调整' }
              ]}
            />
          </Form.Item>
          <Form.Item name="applyDeptId" label="申请科室" rules={[{ required: true, message: '请选择科室' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              style={{ width: 220 }}
              placeholder="搜索并选择科室"
              options={(depts.data ?? []).map((row) => ({ value: row.id, label: row.deptName }))}
            />
          </Form.Item>
          <Form.Item name="returnWarehouseId" label="退回仓" rules={[{ required: true, message: '请选择仓库' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              style={{ width: 220 }}
              placeholder="搜索并选择仓库"
              options={(warehouses.data ?? []).map((row) => ({ value: row.id, label: row.whName }))}
            />
          </Form.Item>
          <Form.Item name="supplierId" label="责任供应商">
            <SupplierSelect onPick={(supplier) => form.setFieldValue('supplierName', supplier?.supplierName ?? '')} />
          </Form.Item>
          <Form.Item name="supplierName" label="供应商名称（快照）">
            <Input placeholder="选定供应商后自动回填" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="sourceBizType" label="原单类型">
            <Input placeholder="如 PURCHASE" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="sourceBizNo" label="原单号">
            <Input placeholder="原单号" style={{ width: 200 }} />
          </Form.Item>
        </Space>

        <Form.Item name="remark" label="备注">
          <Input.TextArea rows={2} maxLength={500} showCount />
        </Form.Item>

        <Typography.Title level={5}>退货明细</Typography.Title>
        <Form.List name="items">
          {(fields, { add, remove }) => (
            <>
              <Table
                size="small"
                pagination={false}
                rowKey="key"
                dataSource={fields}
                scroll={{ x: 1100 }}
                columns={[
                  {
                    title: '物资',
                    width: 220,
                    render: (_, field) => (
                      <Form.Item
                        name={[field.name, 'materialId']}
                        rules={[{ required: true, message: '必填' }]}
                        style={{ margin: 0 }}
                      >
                        <MaterialSelect
                          onPick={(material) => {
                            form.setFieldValue(['items', field.name, 'materialName'], material?.materialName ?? '');
                            form.setFieldValue(['items', field.name, 'unitId'], material?.baseUnitId ?? '');
                            form.setFieldValue(['items', field.name, 'unitName'], material?.unitName ?? '');
                          }}
                        />
                      </Form.Item>
                    )
                  },
                  {
                    title: '品名',
                    width: 180,
                    render: (_, field) => (
                      <Form.Item name={[field.name, 'materialName']} style={{ margin: 0 }}>
                        <Input placeholder="选物资后自动回填" />
                      </Form.Item>
                    )
                  },
                  {
                    title: '单位',
                    width: 120,
                    render: (_, field) => (
                      <>
                        {/* 基本单位由物资主数据带出，不再让人工拍 unit_id */}
                        <Form.Item name={[field.name, 'unitId']} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item name={[field.name, 'unitName']} style={{ margin: 0 }}>
                          <Input disabled placeholder="随物资带出" />
                        </Form.Item>
                      </>
                    )
                  },
                  {
                    title: '批号',
                    width: 130,
                    render: (_, field) => (
                      <Form.Item name={[field.name, 'batchNo']} style={{ margin: 0 }}>
                        <Input placeholder="批号" />
                      </Form.Item>
                    )
                  },
                  {
                    title: '原购买量',
                    width: 120,
                    render: (_, field) => (
                      <Form.Item name={[field.name, 'buyQty']} style={{ margin: 0 }}>
                        <InputNumber min={0} precision={3} style={{ width: '100%' }} />
                      </Form.Item>
                    )
                  },
                  {
                    title: '退货数量',
                    width: 120,
                    render: (_, field) => (
                      <Form.Item
                        name={[field.name, 'returnQty']}
                        rules={[{ required: true, message: '必填' }]}
                        style={{ margin: 0 }}
                      >
                        <InputNumber min={0.001} precision={3} style={{ width: '100%' }} />
                      </Form.Item>
                    )
                  },
                  {
                    title: '单价',
                    width: 130,
                    render: (_, field) => (
                      <Form.Item name={[field.name, 'price']} style={{ margin: 0 }}>
                        <InputNumber min={0} precision={4} style={{ width: '100%' }} />
                      </Form.Item>
                    )
                  },
                  {
                    title: '质量问题描述',
                    width: 180,
                    render: (_, field) => (
                      <Form.Item name={[field.name, 'qualityDesc']} style={{ margin: 0 }}>
                        <Input placeholder="如：外包装破损" />
                      </Form.Item>
                    )
                  },
                  {
                    title: '',
                    width: 56,
                    render: (_, field) => (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={fields.length === 1}
                        onClick={() => remove(field.name)}
                      />
                    )
                  }
                ]}
              />
              <Button
                type="dashed"
                block
                icon={<PlusOutlined />}
                style={{ marginTop: 12 }}
                onClick={() => add({ materialId: '', unitId: '', buyQty: 0, returnQty: 1, price: 0 })}
              >
                添加明细行
              </Button>
            </>
          )}
        </Form.List>

        <Space style={{ marginTop: 24 }}>
          <Button type="primary" htmlType="submit" loading={createMutation.isPending}>
            保存退货单
          </Button>
          <Button onClick={() => navigate('/return')}>返回列表</Button>
          <Typography.Text type="secondary">退货数量不得超过原购买量；金额按数量×单价由后端取负</Typography.Text>
        </Space>
      </Form>
    </Card>
  );
}
